import { z } from 'zod';
import { EQUIPMENT_ICONS } from '../equipment';
import { CHECKLIST_ITEM_KINDS, CHECKLIST_VALUES } from '../checklist';
import { booleanQuery, intSchema, optionalText, requiredText } from './common';

const checklistIdSchema = z
  .string()
  .trim()
  .min(1, 'Identificador inválido')
  .max(40, 'Identificador inválido')
  .regex(/^[A-Za-z0-9_-]+$/, 'Identificador inválido');

export const checklistItemSchema = z.object({
  id: checklistIdSchema,
  label: requiredText(200, 'Nome do item'),
  kind: z.enum(CHECKLIST_ITEM_KINDS, { errorMap: () => ({ message: 'Tipo de item inválido' }) }),
  hint: optionalText(200, 'Método / ferramenta'),
});

export const checklistSectionSchema = z.object({
  id: checklistIdSchema,
  title: requiredText(120, 'Título da seção'),
  note: optionalText(500, 'Observação da seção'),
  items: z.array(checklistItemSchema).max(80, 'Máximo de 80 itens por seção'),
});

export const checklistTemplateSchema = z
  .array(checklistSectionSchema)
  .max(40, 'Máximo de 40 seções')
  .superRefine((sections, ctx) => {
    const seen = new Set<string>();
    sections.forEach((s, si) =>
      s.items.forEach((item, ii) => {
        if (seen.has(item.id)) {
          ctx.addIssue({ code: 'custom', path: [si, 'items', ii, 'id'], message: 'Item duplicado no checklist' });
        }
        seen.add(item.id);
      }),
    );
  });

export const equipmentCategoryCreateSchema = z.object({
  name: requiredText(60, 'Nome da categoria'),
  icon: z.enum(EQUIPMENT_ICONS, { errorMap: () => ({ message: 'Ícone inválido' }) }).default('cpu'),
  usesUnlockPattern: z.boolean().default(false),
  requiresImei: z.boolean().default(false),
  position: intSchema('Ordem', 0, 9999).optional(),
  active: z.boolean().optional(),
  checklist: checklistTemplateSchema.default([]),
  entryChecklist: checklistTemplateSchema.refine(t => t.every(s => s.items.every(i => i.kind === 'test')), 'Entrada aceita apenas itens de estado').default([]),
});
export const equipmentCategoryUpdateSchema = equipmentCategoryCreateSchema.partial();
export type EquipmentCategoryCreateInput = z.infer<typeof equipmentCategoryCreateSchema>;
export type EquipmentCategoryUpdateInput = z.infer<typeof equipmentCategoryUpdateSchema>;
export type EquipmentCategoryFormValues = z.input<typeof equipmentCategoryCreateSchema>;

export const equipmentCategoryListQuerySchema = z.object({ includeInactive: booleanQuery });

/** Salvar respostas do checklist de uma OS. */
export const checklistAnswerSchema = z.object({
  value: z.enum(CHECKLIST_VALUES).nullish(),
  text: z.string().max(2000, 'Observação: máximo de 2000 caracteres').nullish(),
});

export const orderChecklistSaveSchema = z.object({
  answers: z.record(checklistIdSchema, checklistAnswerSchema),
  /** true = trocar a cópia do modelo pela versão atual da categoria (respostas de itens iguais são mantidas). */
  refreshTemplate: z.boolean().optional(),
});
export type OrderChecklistSaveInput = z.infer<typeof orderChecklistSaveSchema>;

export const orderEntryChecklistSaveSchema = z.object({
  answers: z.record(checklistIdSchema, z.object({ value: z.enum(['OK', 'DEFECT', 'NOT_TESTED']).nullish(), text: z.string().max(2000).nullish() })),
  refreshTemplate: z.boolean().optional(),
});
export type OrderEntryChecklistSaveInput = z.infer<typeof orderEntryChecklistSaveSchema>;
